// Rendering of each step in the multi-step questionnaire.
// Extracted from QuestionnairePage.jsx during the Feb 2026 refactor.
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { Globe, DollarSign } from "lucide-react";
import {
  HEALTH_CONDITIONS,
  HOBBIES,
  LANGUAGES,
  INCOME_SOURCES,
  WEATHER_PREFERENCES,
  COUNTRIES_LIST,
} from "./questionnaireConfig";

export const renderQuestionnaireStep = ({
  currentStep,
  formData,
  setFormData,
  toggleArrayItem,
  animating,
}) => {
    const stepClass = animating ? "opacity-0" : "opacity-100 slide-in";
    
    switch (currentStep) {
      case 0: // Personal Info
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Age: {formData.age}</Label>
              <Slider
                value={[formData.age]}
                onValueChange={([value]) => setFormData({ ...formData, age: value })}
                min={18}
                max={85}
                step={1}
                className="py-4"
                data-testid="age-slider"
              />
            </div>
            
            <div className="space-y-3">
              <Label htmlFor="occupation">Current Occupation</Label>
              <Input
                id="occupation"
                placeholder="e.g., Software Engineer, Retired, Teacher"
                value={formData.occupation}
                onChange={(e) => setFormData({ ...formData, occupation: e.target.value })}
                className="h-12 bg-white/50"
                data-testid="occupation-input"
              />
            </div>
            
            <div className="space-y-3">
              <Label>Number of Dependents: {formData.dependents}</Label>
              <Slider
                value={[formData.dependents]}
                onValueChange={([value]) => setFormData({ ...formData, dependents: value })}
                min={0}
                max={10}
                step={1}
                className="py-4"
                data-testid="dependents-slider"
              />
            </div>
          </div>
        );

      case 1: // Duration & Lifestyle
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Intended Length of Stay Overseas</Label>
              <RadioGroup
                value={formData.intended_duration}
                onValueChange={(value) => setFormData({ ...formData, intended_duration: value })}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { value: "less_than_3_months", label: "Less than 3 months", desc: "Short-term stay or trial period" },
                  { value: "less_than_1_year", label: "Less than 1 year", desc: "Extended stay or sabbatical" },
                  { value: "permanent", label: "Permanent relocation", desc: "Making this your new home" },
                  { value: "part_time", label: "Part-time / Split", desc: "Between US and destination" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.intended_duration === option.value
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`duration-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label>Alcohol Consumption Habits</Label>
              <p className="text-sm text-muted-foreground mb-2">This helps us match you with countries that align with your lifestyle preferences and local laws.</p>
              <RadioGroup
                value={formData.alcohol_habits}
                onValueChange={(value) => setFormData({ ...formData, alcohol_habits: value })}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { value: "none", label: "Non-drinker", desc: "I don't consume alcohol" },
                  { value: "rare", label: "Occasional", desc: "A few times a year" },
                  { value: "social", label: "Social drinker", desc: "Regularly in social settings" },
                  { value: "regular", label: "Regular", desc: "Part of my daily/weekly routine" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.alcohol_habits === option.value
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`alcohol-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label>Cannabis Usage</Label>
              <p className="text-sm text-muted-foreground mb-2">Cannabis laws vary significantly by country. This helps us find destinations that match your preferences.</p>
              <RadioGroup
                value={formData.cannabis_habits}
                onValueChange={(value) => setFormData({ ...formData, cannabis_habits: value })}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { value: "none", label: "Non-user", desc: "I don't use cannabis" },
                  { value: "medical", label: "Medical use", desc: "For medical purposes" },
                  { value: "occasional", label: "Occasional", desc: "Recreational, occasionally" },
                  { value: "regular", label: "Regular user", desc: "Legal access is important" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.cannabis_habits === option.value
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`cannabis-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>
          </div>
        );

      case 2: // Family Situation
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div>
                  <Label className="text-base">Children Needing School Enrollment?</Label>
                  <p className="text-sm text-muted-foreground">Will you have children that need to enroll in school?</p>
                </div>
                <Switch
                  checked={formData.has_school_children}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_school_children: checked })}
                  data-testid="school-children-switch"
                />
              </div>
              
              {formData.has_school_children && (
                <div className="space-y-2 pl-4 border-l-2 border-secondary/30">
                  <Label htmlFor="children_ages">Ages of School-Age Children</Label>
                  <Input
                    id="children_ages"
                    placeholder="e.g., 5, 8, 12"
                    value={formData.children_ages}
                    onChange={(e) => setFormData({ ...formData, children_ages: e.target.value })}
                    className="h-12 bg-white/50"
                    data-testid="children-ages-input"
                  />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div>
                  <Label className="text-base">Bringing Elderly Family Members?</Label>
                  <p className="text-sm text-muted-foreground">Will elderly family members be relocating with you?</p>
                </div>
                <Switch
                  checked={formData.bringing_elderly}
                  onCheckedChange={(checked) => setFormData({ ...formData, bringing_elderly: checked })}
                  data-testid="elderly-switch"
                />
              </div>
              
              {formData.bringing_elderly && (
                <div className="space-y-2 pl-4 border-l-2 border-secondary/30">
                  <Label htmlFor="elderly_care_needs">Special Care Needs (if any)</Label>
                  <Input
                    id="elderly_care_needs"
                    placeholder="e.g., Mobility assistance, memory care, regular medical appointments"
                    value={formData.elderly_care_needs}
                    onChange={(e) => setFormData({ ...formData, elderly_care_needs: e.target.value })}
                    className="h-12 bg-white/50"
                    data-testid="elderly-care-input"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 3: // Health & Lifestyle
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Health Conditions (select all that apply)</Label>
              <div className="grid grid-cols-2 gap-3">
                {HEALTH_CONDITIONS.map((condition) => (
                  <label
                    key={condition}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.health_conditions.includes(condition)
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`health-${condition.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Checkbox
                      checked={formData.health_conditions.includes(condition)}
                      onCheckedChange={() => toggleArrayItem("health_conditions", condition)}
                    />
                    <span className="text-sm">{condition}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="space-y-3">
              <Label>Hobbies & Interests (select all that apply)</Label>
              <div className="grid grid-cols-2 gap-3">
                {HOBBIES.map((hobby) => (
                  <label
                    key={hobby}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.hobbies.includes(hobby)
                        ? "border-secondary bg-secondary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`hobby-${hobby.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Checkbox
                      checked={formData.hobbies.includes(hobby)}
                      onCheckedChange={() => toggleArrayItem("hobbies", hobby)}
                    />
                    <span className="text-sm">{hobby}</span>
                  </label>
                ))}
                {/* Other Option */}
                <label
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.hobbies.includes("Other")
                      ? "border-secondary bg-secondary/5"
                      : "border-primary/10 hover:border-primary/30"
                  }`}
                  data-testid="hobby-other"
                >
                  <Checkbox
                    checked={formData.hobbies.includes("Other")}
                    onCheckedChange={() => toggleArrayItem("hobbies", "Other")}
                  />
                  <span className="text-sm">Other</span>
                </label>
              </div>
              
              {/* Other hobby text input */}
              {formData.hobbies.includes("Other") && (
                <div className="mt-3 pl-4 border-l-2 border-secondary/30">
                  <Label htmlFor="other_hobby">Please specify your other hobbies</Label>
                  <Input
                    id="other_hobby"
                    placeholder="e.g., Photography, Scuba diving, Yoga, Cooking classes"
                    value={formData.other_hobby}
                    onChange={(e) => setFormData({ ...formData, other_hobby: e.target.value })}
                    className="h-12 bg-white/50 mt-2"
                    data-testid="other-hobby-input"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 4: // Languages
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Languages You Speak</Label>
              <div className="grid grid-cols-3 gap-3">
                {LANGUAGES.map((lang) => (
                  <label
                    key={lang}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.languages.includes(lang)
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`lang-${lang.toLowerCase()}`}
                  >
                    <Checkbox
                      checked={formData.languages.includes(lang)}
                      onCheckedChange={() => toggleArrayItem("languages", lang)}
                    />
                    <span className="text-sm">{lang}</span>
                  </label>
                ))}
                {/* Other Language Option */}
                <label
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.languages.includes("Other")
                      ? "border-secondary bg-secondary/5"
                      : "border-primary/10 hover:border-primary/30"
                  }`}
                  data-testid="lang-other"
                >
                  <Checkbox
                    checked={formData.languages.includes("Other")}
                    onCheckedChange={() => toggleArrayItem("languages", "Other")}
                  />
                  <span className="text-sm">Other</span>
                </label>
              </div>
              
              {/* Other language text input */}
              {formData.languages.includes("Other") && (
                <div className="mt-3 pl-4 border-l-2 border-secondary/30">
                  <Label htmlFor="other_language">Please specify other languages you speak</Label>
                  <Input
                    id="other_language"
                    placeholder="e.g., Mandarin, Arabic, Hindi, Swahili"
                    value={formData.other_language}
                    onChange={(e) => setFormData({ ...formData, other_language: e.target.value })}
                    className="h-12 bg-white/50 mt-2"
                    data-testid="other-language-input"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 5: // Financial
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Financial Resources</Label>
              <RadioGroup
                value={formData.financial_resources}
                onValueChange={(value) => setFormData({ ...formData, financial_resources: value })}
                className="grid grid-cols-2 gap-3"
              >
                {[
                  { value: "low", label: "Limited Budget", desc: "< $30k/year" },
                  { value: "medium", label: "Moderate", desc: "$30k - $75k/year" },
                  { value: "high", label: "Comfortable", desc: "$75k - $150k/year" },
                  { value: "very_high", label: "Affluent", desc: "> $150k/year" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.financial_resources === option.value
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`financial-${option.value}`}
                  >
                    <RadioGroupItem value={option.value} />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-3">
              <Label>Income Sources</Label>
              <div className="grid grid-cols-2 gap-3">
                {INCOME_SOURCES.map((source) => (
                  <label
                    key={source}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.income_sources.includes(source)
                        ? "border-secondary bg-secondary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`income-${source.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <Checkbox
                      checked={formData.income_sources.includes(source)}
                      onCheckedChange={() => toggleArrayItem("income_sources", source)}
                    />
                    <span className="text-sm">{source}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label htmlFor="target_budget">Target Monthly Budget (optional)</Label>
              <Input
                id="target_budget"
                type="number"
                placeholder="e.g., 3000"
                value={formData.target_monthly_budget}
                onChange={(e) => setFormData({ ...formData, target_monthly_budget: e.target.value })}
                className="h-12 bg-white/50"
                data-testid="target-budget-input"
              />
              <p className="text-sm text-muted-foreground">Your ideal monthly living expenses in USD</p>
            </div>
          </div>
        );

      case 6: // Work & Internet
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div>
                  <Label className="text-base">Do You Intend to Work?</Label>
                  <p className="text-sm text-muted-foreground">Will you be working (employed or self-employed) abroad?</p>
                </div>
                <Switch
                  checked={formData.intend_to_work}
                  onCheckedChange={(checked) => setFormData({ ...formData, intend_to_work: checked })}
                  data-testid="work-switch"
                />
              </div>
              
              {formData.intend_to_work && (
                <div className="pl-4 border-l-2 border-secondary/30">
                  <Label>Work Type</Label>
                  <RadioGroup
                    value={formData.work_type}
                    onValueChange={(value) => setFormData({ ...formData, work_type: value })}
                    className="grid grid-cols-3 gap-3 mt-2"
                  >
                    {[
                      { value: "remote", label: "Remote/Online" },
                      { value: "local", label: "Local Employment" },
                      { value: "business", label: "Own Business" }
                    ].map((option) => (
                      <label
                        key={option.value}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                          formData.work_type === option.value
                            ? "border-primary bg-primary/5"
                            : "border-primary/10 hover:border-primary/30"
                        }`}
                        data-testid={`work-type-${option.value}`}
                      >
                        <RadioGroupItem value={option.value} />
                        <span className="text-sm">{option.label}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div>
                  <Label className="text-base">Require High-Speed Internet?</Label>
                  <p className="text-sm text-muted-foreground">Do you need dependable high-speed internet access?</p>
                </div>
                <Switch
                  checked={formData.require_high_speed_internet}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_high_speed_internet: checked })}
                  data-testid="internet-switch"
                />
              </div>
              
              {formData.require_high_speed_internet && (
                <div className="space-y-3 pl-4 border-l-2 border-secondary/30">
                  <Label>Minimum Internet Speed: {formData.internet_speed_requirement} Mbps</Label>
                  <Slider
                    value={[parseInt(formData.internet_speed_requirement)]}
                    onValueChange={([value]) => setFormData({ ...formData, internet_speed_requirement: value.toString() })}
                    min={25}
                    max={500}
                    step={25}
                    className="py-4"
                    data-testid="internet-speed-slider"
                  />
                  <p className="text-sm text-muted-foreground">
                    {parseInt(formData.internet_speed_requirement) >= 100 ? "Suitable for video calls and streaming" : 
                     parseInt(formData.internet_speed_requirement) >= 50 ? "Good for regular remote work" : 
                     "Basic browsing and email"}
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 7: // Safety & Stability Preferences
        return (
          <div className={`space-y-8 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-4">
              <Label>Low Crime Importance: {formData.low_crime_importance}/5</Label>
              <Slider
                value={[formData.low_crime_importance]}
                onValueChange={([value]) => setFormData({ ...formData, low_crime_importance: value })}
                min={1}
                max={5}
                step={1}
                className="py-4"
                data-testid="crime-slider"
              />
              <p className="text-sm text-muted-foreground">How important is living in a low-crime area?</p>
            </div>

            <div className="space-y-4">
              <Label>Government Stability: {formData.government_stability_importance}/5</Label>
              <Slider
                value={[formData.government_stability_importance]}
                onValueChange={([value]) => setFormData({ ...formData, government_stability_importance: value })}
                min={1}
                max={5}
                step={1}
                className="py-4"
                data-testid="stability-slider"
              />
              <p className="text-sm text-muted-foreground">How important is political stability?</p>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
              <div>
                <Label className="text-base">Low Natural Disaster Risk</Label>
                <p className="text-sm text-muted-foreground">Prefer countries with low risk of earthquakes, hurricanes, floods, etc.?</p>
              </div>
              <Switch
                checked={formData.low_natural_disaster_risk}
                onCheckedChange={(checked) => setFormData({ ...formData, low_natural_disaster_risk: checked })}
                data-testid="disaster-switch"
              />
            </div>

            <div className="space-y-4">
              <Label>Travel Ease: {formData.travel_ease_importance}/5</Label>
              <Slider
                value={[formData.travel_ease_importance]}
                onValueChange={([value]) => setFormData({ ...formData, travel_ease_importance: value })}
                min={1}
                max={5}
                step={1}
                className="py-4"
                data-testid="travel-slider"
              />
              <p className="text-sm text-muted-foreground">How important is easy travel in/out of the country?</p>
            </div>

            <div className="space-y-4">
              <Label>Healthcare Quality: {formData.healthcare_importance}/5</Label>
              <Slider
                value={[formData.healthcare_importance]}
                onValueChange={([value]) => setFormData({ ...formData, healthcare_importance: value })}
                min={1}
                max={5}
                step={1}
                className="py-4"
                data-testid="healthcare-slider"
              />
              <p className="text-sm text-muted-foreground">How important is access to quality healthcare?</p>
            </div>

            <div className="space-y-4">
              <Label>Expat Community: {formData.expat_community_importance}/5</Label>
              <Slider
                value={[formData.expat_community_importance]}
                onValueChange={([value]) => setFormData({ ...formData, expat_community_importance: value })}
                min={1}
                max={5}
                step={1}
                className="py-4"
                data-testid="expat-community-slider"
              />
              <p className="text-sm text-muted-foreground">How important is having a thriving expat community?</p>
            </div>
          </div>
        );

      case 8: // Climate & Environment
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-3">
              <Label>Weather Preference (check all that apply)</Label>
              <div className="grid grid-cols-2 gap-3">
                {WEATHER_PREFERENCES.map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.weather_preferences.includes(option.value)
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`weather-${option.value}`}
                  >
                    <Checkbox
                      checked={formData.weather_preferences.includes(option.value)}
                      onCheckedChange={() => toggleArrayItem("weather_preferences", option.value)}
                    />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>Climate Preference (check all that apply)</Label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "tropical", label: "Tropical", desc: "Hot & humid year-round" },
                  { value: "mediterranean", label: "Mediterranean", desc: "Warm summers, mild winters" },
                  { value: "temperate", label: "Temperate", desc: "Four distinct seasons" },
                  { value: "cold", label: "Cold", desc: "Cool to cold most of year" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.climate_preferences.includes(option.value)
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`climate-${option.value}`}
                  >
                    <Checkbox
                      checked={formData.climate_preferences.includes(option.value)}
                      onCheckedChange={() => toggleArrayItem("climate_preferences", option.value)}
                    />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-sm text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label>City Preference (check all that apply)</Label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: "urban", label: "Urban", desc: "Big city life" },
                  { value: "suburban", label: "Suburban", desc: "Best of both worlds" },
                  { value: "rural", label: "Rural", desc: "Quiet countryside" }
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                      formData.city_preferences.includes(option.value)
                        ? "border-primary bg-primary/5"
                        : "border-primary/10 hover:border-primary/30"
                    }`}
                    data-testid={`city-${option.value}`}
                  >
                    <Checkbox
                      checked={formData.city_preferences.includes(option.value)}
                      onCheckedChange={() => toggleArrayItem("city_preferences", option.value)}
                    />
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 9: // Additional Options
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            {/* Compare Specific Countries */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-primary" />
                  <div>
                    <Label className="text-base">Include Specific Countries</Label>
                    <p className="text-sm text-muted-foreground">Select countries you want included in the comparison (select all that apply)</p>
                  </div>
                </div>
                <Switch
                  checked={formData.compare_specific_country}
                  onCheckedChange={(checked) => setFormData({ ...formData, compare_specific_country: checked, specific_countries: [] })}
                  data-testid="compare-country-switch"
                />
              </div>
              
              {formData.compare_specific_country && (
                <div className="pl-4 border-l-2 border-secondary/30">
                  <Label>Select Countries to Include (select all that apply)</Label>
                  <div className="grid grid-cols-4 gap-2 mt-2 max-h-48 overflow-y-auto">
                    {COUNTRIES_LIST.map((country) => (
                      <label
                        key={country}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                          formData.specific_countries.includes(country)
                            ? "border-primary bg-primary/5"
                            : "border-primary/10 hover:border-primary/30"
                        }`}
                        data-testid={`country-${country.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Checkbox
                          checked={formData.specific_countries.includes(country)}
                          onCheckedChange={() => {
                            const updated = formData.specific_countries.includes(country)
                              ? formData.specific_countries.filter(c => c !== country)
                              : [...formData.specific_countries, country];
                            setFormData({ ...formData, specific_countries: updated });
                          }}
                        />
                        <span>{country}</span>
                      </label>
                    ))}
                  </div>
                  {formData.specific_countries.length > 0 && (
                    <p className="text-sm text-accent mt-2">
                      Selected: {formData.specific_countries.join(", ")}
                    </p>
                  )}
                  
                  {/* Other Country Text Box */}
                  <div className="mt-4">
                    <Label htmlFor="other_country">Other country not listed above?</Label>
                    <Input
                      id="other_country"
                      placeholder="e.g., Montenegro, Slovenia, Belize"
                      value={formData.other_country}
                      onChange={(e) => setFormData({ ...formData, other_country: e.target.value })}
                      className="h-12 bg-white/50 mt-2"
                      data-testid="other-country-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Solve for Budget */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/10 bg-white/50">
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-secondary" />
                  <div>
                    <Label className="text-base">Solve for a Specific Budget</Label>
                    <p className="text-sm text-muted-foreground">Find countries that fit your exact budget</p>
                  </div>
                </div>
                <Switch
                  checked={formData.solve_for_budget}
                  onCheckedChange={(checked) => setFormData({ ...formData, solve_for_budget: checked })}
                  data-testid="budget-switch"
                />
              </div>
              
              {formData.solve_for_budget && (
                <div className="space-y-3 pl-4 border-l-2 border-secondary/30">
                  <Label htmlFor="budget_amount">Monthly Budget (USD)</Label>
                  <Input
                    id="budget_amount"
                    type="number"
                    placeholder="e.g., 2500"
                    value={formData.budget_amount}
                    onChange={(e) => setFormData({ ...formData, budget_amount: e.target.value })}
                    className="h-12 bg-white/50"
                    data-testid="budget-amount-input"
                  />
                  <p className="text-sm text-muted-foreground">
                    We'll prioritize countries where you can live comfortably on this budget
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 10: // Final Thoughts
        return (
          <div className={`space-y-6 transition-opacity duration-200 ${stepClass}`}>
            <div className="space-y-4">
              <Label htmlFor="additional_info">Is there any other information we should consider?</Label>
              <textarea
                id="additional_info"
                placeholder="Tell us about any specific requirements, concerns, or preferences that weren't covered in the previous questions..."
                value={formData.additional_info}
                onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                className="w-full h-32 p-4 rounded-lg border border-primary/10 bg-white/50 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="additional-info-textarea"
              />
              <p className="text-sm text-muted-foreground">
                This helps us provide more personalized recommendations
              </p>
            </div>

            {/* Summary */}
            <div className="bg-accent/10 rounded-xl p-6 border border-accent/20">
              <h3 className="font-semibold text-primary mb-3">Ready to find your match!</h3>
              <p className="text-sm text-muted-foreground">
                Based on your responses, we'll analyze countries across safety, cost of living, 
                healthcare, visa accessibility, expat community, and more to find your ideal destinations.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };
